import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import LanguageSwitcher from './LanguageSwitcher';
import { useAppSelector } from '../store/hooks';
import Avatar from './common/Avatar';
import logoImage from '../assets/logo-l.png';
import { API_BASE_URL } from '../services/api';

const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  width: 100%;
  /* Solid white background for the landing page header */
  background: #ffffff;
  backdrop-filter: blur(10px);
  z-index: 1000;
  border-bottom: 1px solid var(--border-light);
  transition: all 0.3s ease;
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  max-width: 1200px;
  margin: 0 auto;
  padding-left: 20px;
  padding-right: 20px;

  @media (min-width: 1241px) {
    padding-left: 0;
    padding-right: 0;
  }

  @media (max-width: 768px) {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const LogoLink = styled(Link)`
  text-decoration: none;
  cursor: pointer;
`;

const LogoImage = styled.img`
  height: 28px;
  width: auto;
  display: block;
`;

const NavMenu = styled.ul<{ isOpen: boolean }>`
  display: flex;
  list-style: none;
  gap: 2rem;
  margin: 0;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    top: 70px;
    left: 0;
    width: 100%;
    height: calc(100vh - 70px);
    background: var(--header-bg);
    flex-direction: column;
    justify-content: flex-start;
    padding: 2rem;
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform 0.3s ease;
    box-shadow: ${props => props.isOpen ? '0 4px 20px rgba(0, 0, 0, 0.3)' : 'none'};
  }
`;

const NavItem = styled.li`
  a {
    color: var(--header-text);
    font-weight: 500;
    transition: color 0.3s ease;
    
    &:hover {
      color: var(--primary-orange);
    }
  }
`;


const PortalBtn = styled.button`
  background: var(--primary-orange);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  
  &:hover {
    background: #e55b40;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 102, 72, 0.3);
  }
`;

const MobileMenuBtn = styled.button`
  display: none;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: var(--header-text);
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const Header: React.FC = () => {
  const location = useLocation();
  // Hide landing header visually on any portal route (keep mounted to avoid unmount side-effects)
  const isPortal = location.pathname.startsWith('/portal');
  // Keep landing header visible on auth pages within /portal (login, forgot-password, register)
  const isPortalAuth = location.pathname === '/portal/login' || location.pathname === '/portal/forgot-password' || location.pathname === '/portal/register';
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAppSelector((s) => s.auth);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const getInitials = () => {
    const fn = (user?.firstName || '').trim();
    const ln = (user?.lastName || '').trim();
    if (fn || ln) {
      return `${fn.charAt(0)}${ln.charAt(0)}` || (user?.username || user?.email || 'U').charAt(0);
    }
    const fromUsername = (user?.username || '').charAt(0);
    const fromEmail = (user?.email || '').charAt(0);
    return (fromUsername || fromEmail || 'U').toUpperCase();
  };

  const goToProfile = () => {
    if (!user) return;
    navigate('/portal/profile', { state: { scrollTo: 'profile' } });
  };

  const profilePic = user?.profilePicture || user?.profileImage || undefined;

  return (
    <HeaderWrapper style={{ display: (isPortal && !isPortalAuth) ? 'none' : undefined }}>
      <Nav>
        <LogoLink to="/">
            <LogoImage src={logoImage} alt="DaaS logo" />
        </LogoLink>
        
        <NavMenu isOpen={isMenuOpen}>

          <NavItem>
            <LanguageSwitcher onLanguageChange={closeMenu} />
          </NavItem>
          
          <NavItem>
            {isAuthenticated && user ? (
              <button type="button" onClick={(e) => { e.preventDefault(); goToProfile(); closeMenu(); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }} aria-label="Profile">
                <Avatar 
                  size={40} 
                  rounded 
                  src={`${API_BASE_URL}/api/users/me/avatar`} 
                  fallbackSrc={user.profilePicture || user.profileImage || undefined}
                  initials={getInitials()} 
                />
              </button>
            ) : (
              <PortalBtn onClick={() => { navigate('/?auth=login'); closeMenu(); }}>
                {t('nav.studentPortal')}
              </PortalBtn>
            )}
          </NavItem>
        </NavMenu>
        
        <MobileMenuBtn onClick={toggleMenu}>
          ☰
        </MobileMenuBtn>
      </Nav>
    </HeaderWrapper>
  );
};

export default Header;